/*
 * `m:ss`. Three copies of this existed — two byte-identical (`BrowseCard`,
 * `LibraryCard`) and one in `vttCues` that differed in two ways that turn out
 * to be deliberate, which is why this file exports two functions rather than
 * folding them into one.
 */

/** The shared assembly: whole seconds -> `4:07`. Minutes are not zero-padded. */
function clock(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
}

/**
 * A video's length for a duration badge, or `null` when there isn't one.
 *
 * Null rather than `0:00` for an absent value: a queued job has no duration
 * yet, and a badge reading zero is a guess presented as fact. Rounds, because
 * a 4-minute-59.6-second video is a 5:00 video.
 */
export function formatDuration(seconds: number | undefined | null): string | null {
  if (!seconds || Number.isNaN(seconds) || seconds <= 0) return null;
  return clock(Math.round(seconds));
}

/**
 * A position in the timeline, for the timestamp beside a transcript line or a
 * workpad note.
 *
 * Floors rather than rounds — at 7.9s you are still inside the line that began
 * at 0:07, and labelling it 0:08 would point at the next one. Always returns a
 * string, and clamps negatives, because a timeline has a fixed slot to fill.
 */
export function formatTimecode(seconds: number): string {
  // NaN survives both Math.floor and Math.max, and the player clock has to
  // render *something* while `duration` is still NaN-ish metadata — `0:00` is
  // the honest zero position, "NaN:NaN" is a bug on screen.
  if (!Number.isFinite(seconds)) return clock(0);
  return clock(Math.max(0, Math.floor(seconds)));
}
